import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ClipboardList,
  CalendarCheck,
  HelpCircle,
  Signpost,
  Users,
  CalendarClock,
  Notebook,
  Smartphone,
} from "lucide-react";
import ProductVisual from "@/components/ProductVisual";
import Marquee from "@/components/Marquee";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { EtsyButton } from "@/components/BuyButtons";
import { HERO, BENTO_CARDS, IMAGES, FALLBACK_JOURNAL, PAGE_GUIDES } from "@/data/site";

const ICONS = {
  "clipboard-list": ClipboardList,
  "calendar-check": CalendarCheck,
  "help-circle": HelpCircle,
  signpost: Signpost,
  users: Users,
  "calendar-clock": CalendarClock,
  notebook: Notebook,
  smartphone: Smartphone,
};

const TONES = {
  lav: "bg-lav-soft text-lav-deep",
  mint: "bg-mint-soft text-mint-deep",
  blush: "bg-blush-soft text-blush-deep",
};

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { delay: 0.2 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
});

function Hero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 lg:pt-44 lg:pb-24">
      <div className="pointer-events-none absolute -top-32 right-0 h-[480px] w-[480px] rounded-full bg-lav-soft/70 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-mint-soft/70 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>{HERO.eyebrow}</Eyebrow>
          </motion.div>
          <h1 className="mt-7 font-display text-4xl font-semibold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-[4.2rem]">
            {HERO.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-lav-deep" : ""}`}
                  {...lineAnim(i)}
                  data-testid={`hero-line-${i + 1}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg"
            data-testid="hero-subline"
          >
            {HERO.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-lav-deep hover:shadow-xl hover:shadow-lav-soft"
            >
              Shop the Journal
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-cta-learn"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-8 py-4 font-bold text-ink transition-all duration-300 hover:border-lav hover:text-lav-deep"
            >
              Learn More
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96, rotate: 1 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <ProductVisual />
        </motion.div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="py-20 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal className="order-2 lg:order-1">
          <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-3xl shadow-xl shadow-lav-soft/60">
            <img
              src={IMAGES.flatlay}
              alt="A patient organizing their health notes in a journal"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow tone="mint">Why In Your Corner exists</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]"
              data-testid="why-headline"
            >
              When one doctor becomes five, your health notes end up everywhere.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-ink-soft sm:text-lg">
              <p>
                Appointments, medications, symptoms, provider information, questions, referrals, instructions,
                follow-up steps—it's a lot to hold. And when you see more than one healthcare provider, keeping
                it all organized gets even harder.
              </p>
              <p>
                In Your Corner Healthcare gives all of it one home. A simple journal you can bring to every
                appointment, update in minutes, and actually find again when you need it.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 border-l-2 border-blush pl-5 font-display text-xl italic text-ink">
              "Know your health. Know your questions. Know your next step."
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Bento() {
  const spans = [
    "lg:col-span-5",
    "lg:col-span-4",
    "lg:col-span-3",
    "lg:col-span-3",
    "lg:col-span-4",
    "lg:col-span-5",
    "lg:col-span-6",
    "lg:col-span-6",
  ];
  return (
    <section className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow tone="blush">Inside the pages</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem]"
              data-testid="bento-headline"
            >
              What can your journal help you do?
            </h2>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
          {BENTO_CARDS.map((card, i) => {
            const Icon = ICONS[card.icon];
            return (
              <Reveal key={card.id} delay={(i % 3) * 0.07} className={`${spans[i]} sm:col-span-1`}>
                <article
                  data-testid={`bento-card-${card.id}`}
                  className="group h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70 sm:p-8"
                >
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${TONES[card.tone]}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-ink">{card.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{card.body}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="relative overflow-hidden bg-lav-deep py-24 lg:py-36">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blush/25 blur-3xl animate-breathe" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/60">Our philosophy</p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2
            className="mt-7 font-display text-3xl font-semibold leading-snug tracking-tight text-white sm:text-4xl lg:text-[3rem] lg:leading-[1.2]"
            data-testid="philosophy-headline"
          >
            Real life doesn't come with a reset button—but your health information should have one home.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            One journal for every appointment, medication, symptom, and question—so you always know your next
            step.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ProductTeaser() {
  return (
    <section className="py-20 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <Eyebrow>The first product</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem]" data-testid="product-teaser-title">
              In Your Corner Healthcare Journal
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-soft sm:text-lg">
              Track medications and symptoms, prepare for appointments, and keep every question for your doctor
              in one place—fillable on any device, or printed at home.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-7 flex items-end gap-3" data-testid="product-teaser-price">
              <span className="font-display text-4xl font-semibold text-ink">{FALLBACK_JOURNAL.price_label}</span>
              <span className="pb-1.5 text-sm text-ink-faint">{FALLBACK_JOURNAL.price_note}</span>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <EtsyButton href={FALLBACK_JOURNAL.buy_etsy_url} testId="product-teaser-buy-etsy" />
              <Link
                to="/shop"
                data-testid="product-teaser-details"
                className="group inline-flex items-center gap-2 font-bold text-lav-deep transition-colors hover:text-ink"
              >
                See full details
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <ProductVisual chips={false} />
        </Reveal>
      </div>
    </section>
  );
}

function HowItHelpsTeaser() {
  const chips = PAGE_GUIDES.slice(1, 4);
  return (
    <section className="pb-24 lg:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-mint-soft px-6 py-14 sm:px-12 lg:px-20 lg:py-20">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/60 blur-3xl" />
            <div className="relative grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="how-it-helps-teaser-title">
                  Want to see exactly how it helps?
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-soft sm:text-lg">
                  Every page of the journal has a job—from tracking medications to remembering your questions
                  for the doctor. Take a look inside before you buy.
                </p>
                <Link
                  to="/journaling"
                  data-testid="how-it-helps-teaser-cta"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-mint-deep"
                >
                  See how it helps
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="space-y-4">
                {chips.map((g, i) => (
                  <div
                    key={g.id}
                    className="rounded-2xl border border-ink/5 bg-white/80 px-5 py-4 backdrop-blur-sm"
                    style={{ transform: `rotate(${i % 2 === 0 ? -1 : 1}deg)` }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-mint-deep">Page {i + 2}</p>
                    <p className="mt-1 font-display text-base text-ink">{g.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="page-home">
      <Hero />
      <Marquee />
      <Why />
      <Bento />
      <Philosophy />
      <ProductTeaser />
      <HowItHelpsTeaser />
    </main>
  );
}
